import { FY_MONTH, statusTypes } from "./helper";
import ArrowForward from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { useCallback, useContext, useEffect, useState } from "react";
import AppContext from "../../utils/context";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import TrainingEnrollmentForm from "../training-enrollment";
import CloseIcon from "@mui/icons-material/Close";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: "10px 20px",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const CalendarMonth = () => {
  const { userDetails = {} } = useContext(AppContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { monthName, trainingDates, year } = location?.state || {};
  const [eventList, setEventList] = useState([]);
  const selectedMonthIndex = monthName && FY_MONTH.indexOf(monthName);
  const actualMonthIndex = ((selectedMonthIndex + 3) % 12) + 1;
  const displayYear = year && selectedMonthIndex >= 9 ? year + 1 : year;
  const [initialDate, setInitialDate] = useState(
    `${displayYear}-${String(actualMonthIndex).padStart(2, "0")}-01`
  );
  const [showEnrollmentForm, setShowEnrollmentForm] = useState(false);
  const [selectedScheduledDayDetails, setSelectedScheduledDayDetails] =
    useState({
      starteDate: null,
      endDate: null,
      courseId: "",
    });
  const handleBackNavigation = () => {
    userDetails.role === "ADMIN"
      ? navigate("/admin/training/training-year-view")
      : navigate("/oem/training/training-year-view");
  };

  const renderScheduledEvents = useCallback(() => {
    const updatedEventList = transformToFullCalendarEvents(trainingDates);
    setEventList(updatedEventList);
  }, [trainingDates]);

  useEffect(() => {
    if (displayYear && actualMonthIndex) {
      const newInitialDate = `${displayYear}-${String(
        actualMonthIndex
      ).padStart(2, "0")}-01`;
      setInitialDate(newInitialDate);
    }
  }, [displayYear, actualMonthIndex]);

  useEffect(() => {
    if (trainingDates && trainingDates.length > 0) {
      renderScheduledEvents();
    }
  }, [trainingDates, renderScheduledEvents]);

  const renderEventContent = (eventInfo) => {
    const { className } = eventInfo.event.extendedProps;
    return (
      <Tooltip
        title={`View Training Request Details for ${eventInfo.event.title}`}
      >
        <div className={`custom-event ${className}`}>
          <b>{eventInfo.timeText}</b>
          <i>{eventInfo.event.title}</i>
        </div>
      </Tooltip>
    );
  };

  const handleEventClick = (info) => {
    if (userDetails.role === "ADMIN") {
      navigate("/admin/training/training-requested-list", {
        state: {
          startDate: info.event.startStr,
          endDate: info.event.endStr || info.event.startStr,
          selectedMonth: monthName,
        },
      });
    } else {
      const { trainingStatus } = info.event.extendedProps;
      !["completed", "booked"].includes(trainingStatus) &&
        setShowEnrollmentForm(true);
      setSelectedScheduledDayDetails({
        startDate: info?.event?.startStr || info.dateStr,
        endDate: info?.event?.endStr || info?.event?.startStr || info?.dateStr,
        courseId: info?.event?.id || "",
      });
    }
  };

  const formatDate = (year, month, day) => {
    const monthNumber = new Date(`${month} 1, ${year}`).getMonth() + 1; // Convert month name to number
    const paddedMonth = String(monthNumber).padStart(2, "0");
    const paddedDay = String(day).padStart(2, "0");
    return `${year}-${paddedMonth}-${paddedDay}`;
  };

  // const addOneDay = (dateString) => {
  //   const date = new Date(dateString);
  //   date.setDate(date.getDate() + 1);
  //   return date.toISOString().split("T")[0]; // Convert back to "YYYY-MM-DD"
  // };
  const addOneDay = (dateString) => {
    const date = new Date(dateString);

    if (!(date instanceof Date) || isNaN(date.getTime())) {
      console.warn("Invalid date:", dateString);
      return null;
    }

    date.setDate(date.getDate() + 1);
    return date.toISOString().split("T")[0]; // Format: YYYY-MM-DD
  };
  // Transform JSON data into FullCalendar format
  const transformToFullCalendarEvents = (data) => {
    const events = [];
    const ongoingEvents = {}; // To track multi-day events by courseId

    data.forEach((item) => {
      const { year, month, day, type, courseId, model, status } = item;
      const date = formatDate(year, month, day);

      if (type === "single") {
        // Single-day event
        events.push({
          title: `${model}`,
          start: date,
          trainingStatus: status,
          className:
            userDetails.role === "ADMIN"
              ? `single-day-event-${status}-admin`
              : `single-day-event-${status}`,
        });
      } else if (type === "start") {
        // Start of a multi-day event
        ongoingEvents[courseId] = { start: date };
      } else if (type === "range") {
        // Range (multi-day events spanning multiple days)
        if (ongoingEvents[courseId]) {
          ongoingEvents[courseId].end = date;
        }
      } else if (type === "end") {
        // End of a multi-day event
        if (ongoingEvents[courseId]) {
          const endDate = addOneDay(date); // Add one day to the end date
          events.push({
            title: `${model}`,
            start: ongoingEvents[courseId].start,
            end: endDate, // FullCalendar end date is exclusive
            trainingStatus: status,
            className:
              userDetails.role === "ADMIN"
                ? `multi-day-event-${status}-admin`
                : `multi-day-event-${status}`,
          });
          delete ongoingEvents[courseId]; // Clear completed event
        }
      }
    });
    return events;
  };

  const handleCloseEnrollment = () => {
    setShowEnrollmentForm(false);
  };

  return (
    <>
      {trainingDates?.length ? (
        <>
          <div className="training-title-container">
            <ArrowForward
              fontSize="small"
              onClick={() => handleBackNavigation()}
            />
            <div>
              <span className="training-title">Training</span>
              <div className="training-schedule">Past year schedule</div>
            </div>
            <div className="training-year">
              {monthName} {displayYear}
            </div>
          </div>
          <div className="calendar-month-container">
            <div className="month-view-container">
              <FullCalendar
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                initialDate={initialDate}
                events={eventList}
                eventContent={renderEventContent} // Optional if you want custom rendering
                dateClick={handleEventClick}
                eventClick={handleEventClick}
              />
              <div className="training-status-legend">
                {statusTypes.map((data) => (
                  <div className={`legend-${data.toLowerCase()}`}>{data}</div>
                ))}
              </div>
            </div>
            {/* 
            <div className="details-container">
                <div className="details-title">Details Day</div>
                <div className="details-sub-title">Don't miss scheduled events</div>
                {
                    currentMonthTrainings.length ? currentMonthTrainings.map((training)=>{
                        return(
                            <div className="training-card">
                                <div className="card-header">
                                    <span>CR Training</span>
                                    <span className="time-tag">{training.startTime} to {training.endTime}</span>
                                </div>
                                <div className="training-location">{training.location}</div>
                                <div className="training-date"><img src={CalendarIcon} alt={CalendarIcon}/> {training.dayOfWeek},{monthName},{year} </div>
                            </div>
                        )
                    }) : 
                    <div className='no-events-details'>No Events scheduled</div>
                }
            </div>  */}
          </div>
        </>
      ) : (
        <div>
          <div className="training-title-container">
            <ArrowForward
              fontSize="small"
              onClick={() => handleBackNavigation()}
            />
            <div>
              <span className="training-title">Training</span>
              <div className="training-schedule">Past year schedule</div>
            </div>
            <div className="training-year">
              {monthName} {displayYear}
            </div>
          </div>
          <Typography
            variant="h4"
            textAlign="center"
            sx={{ mt: 3, color: "gray" }}
          >
            Training schedule not available
          </Typography>
        </div>
      )}

      {showEnrollmentForm && (
        <div>
          <BootstrapDialog
            onClose={() => handleCloseEnrollment()}
            aria-labelledby="customized-dialog-title"
            open={showEnrollmentForm}
            fullWidth
            maxWidth="lg"
          >
            <DialogTitle
              sx={{
                m: 0,
                p: 2,
                "&.MuiDialogTitle-root": { "font-weight": "600" },
              }}
              id="customized-dialog-title"
            >
              TRAINING ENROLLMENT
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={() => handleCloseEnrollment()}
              sx={(theme) => ({
                position: "absolute",
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
              })}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent
              dividers
              sx={{ "&.MuiDialogContent-dividers": { border: 0 } }}
            >
              <TrainingEnrollmentForm {...selectedScheduledDayDetails} />
            </DialogContent>
          </BootstrapDialog>
        </div>
      )}
    </>
  );
};

export default CalendarMonth;
